import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import EditButton from '../../../EditButton';
import ExportModal from '../../TransactionsPage/ExportModal';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow/AuthenticationWindow';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {transactionActions} from '../../../../state/actions/transaction.actions';
import {scaHelpers, systemHelpers} from '../../../../utils/helpers';
import {SCAActionsConstants} from '../../../../constants';
import {firebaseEvents} from '../../../../snippets/firebase';
import {DownloadIcon} from '../../../../icons';

const {logEvent} = systemHelpers;

const SCA_OPERATION_NAME = SCAActionsConstants.TRANSACTIONS_DOWNLOAD_PDF_AND_CSV_ACTION;

const exportTimeFormat = 'YYYY-MM-DD';

const validateDateRange = (dateRange) => dateRange && dateRange.length === 2;

const ExportVendorTransactionsModal = ({
  downloadTransactions,
  downloadZipInvoices,
  vendor,
}) => {
  const [t] = useTranslation(['main', 'cards', 'transactions']);
  const [exportModalProps, setExportModalProps] = useState({loading: false, open: false});
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [exportDetails, setExportDetails] = useState({range: undefined, format: null});

  const vendorId = useMemo(() => vendor?.vendor_id, [vendor]);

  const handleExportErrorCallback = () => setExportModalProps({...exportModalProps, open: true, loading: false, error: t('fileIsTooLargeToDownloadMessage')});

  const handleExportZip = (data) => {
    const {date_range: dateRange} = data;

    const isValidDateRange = validateDateRange(dateRange);

    if (!isValidDateRange) return;

    const query = {
      date_gte: dateRange[0].format(exportTimeFormat),
      date_lte: dateRange[1].format(exportTimeFormat),
      vendor_local_id: vendorId
    };

    downloadZipInvoices({
      query,
      successCallback: (data) => {
        window.open(data.url, 'download');
        handleCancelExportModal();
        logEvent(firebaseEvents.TRANSACTIONS_DOWNLOAD_ZIP_INVOICES);
      },
      errorCallback: handleExportErrorCallback
    });
  }

  const handleExportTransactions = (data) => {
    const {format, date, date_range: dateRange} = data;

    const isValidDateRange = validateDateRange(dateRange);

    const [startDate, endDate] = format === 'csv' && isValidDateRange
      ? dateRange
      : [date.startOf('month'), date.endOf('month')];

    const downloadLogEvents = {
      csv: firebaseEvents.TRANSACTIONS_EXPORT_CSV,
    };

    const query = {
      date_gte: startDate.format(exportTimeFormat),
      date_lte: endDate.format(exportTimeFormat),
      file_format: format,
    };

    downloadTransactions({
      id: vendorId,
      headers: scaHelpers.getAuthHeaders(SCA_OPERATION_NAME),
      query,
      successCallback: (data) => {
        window.open(data.url, 'download');
        handleCancelExportModal();
      },
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => {
            const downloadLogEvent = downloadLogEvents[format];
            setAuthWindowProps({...authWindowProps, ...scaProps});
            setExportDetails(data);
            downloadLogEvent && logEvent(downloadLogEvent);
          },
          handleExportErrorCallback
        });
      }
    });
  }

  const handleOnExportClick = () => setExportModalProps({...exportModalProps, open: true});

  const handleOkExportModal = (data) => {
    if (!vendorId) return;

    setExportModalProps({...exportModalProps, loading: true});

    const exportHandler = data.format === 'zip' ? handleExportZip : handleExportTransactions;
    exportHandler(data);
  }

  const handleCancelExportModal = () => setExportModalProps({...exportModalProps, open: false, loading: false, error: null});

  const handleCloseAuthModal = () => {
    setAuthWindowProps({...authWindowProps, open: false});
    setExportModalProps({...exportModalProps, loading: false});
  }

  const handleOnSuccessAuth = () => {
    handleCloseAuthModal();
    handleOkExportModal(exportDetails);
  }

  return (
    <>
      <EditButton
        loading={exportModalProps.loading}
        onClick={handleOnExportClick}
        icon={<DownloadIcon />}
        tooltipProps={{title: t('export')}}
      />

      <ExportModal
        {...exportModalProps}
        handleCancel={handleCancelExportModal}
        handleOk={handleOkExportModal}
        options={['csv', 'zip']}
        title={`${t('export')} ${t('transactions')}`}
      />

      <AuthenticationWindow
        {...authWindowProps}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleOnSuccessAuth}
        operationName={SCA_OPERATION_NAME}
      />
    </>
  );
}

ExportVendorTransactionsModal.propTypes = {
  vendor: PropTypes.shape({
    beneficiary_id: PropTypes.string,
    vendor_id: PropTypes.string
  }),
}

const mapDispatchToProps = {
  downloadTransactions: transactionActions.downloadVendorTransactions,
  downloadZipInvoices: expenseActions.downloadZipInvoices
}

export default connect(null, mapDispatchToProps)(ExportVendorTransactionsModal);
